import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Lightbulb, Plus } from "lucide-react";
import { format } from "date-fns";
import { useAuth } from "@/hooks/useAuth";
import SubmitSuggestionDialog from "@/components/submit-suggestion-dialog";
import SuggestionContent from "@/components/suggestion-content";

interface PublicSuggestion {
  id: string;
  content: any;
  plainContent: string;
  screenshots: Array<{ name: string; data: string }>;
  displayName: string;
  createdAt: Date;
}

export default function SuggestionsPage() {
  const { isAdmin } = useAuth();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  
  const { data: suggestions = [], isLoading } = useQuery<PublicSuggestion[]>({
    queryKey: ["/api/suggestions"],
  });

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="mb-6">
          <Skeleton className="h-8 w-64 mb-2" />
          <Skeleton className="h-4 w-96" />
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Card key={i}>
              <CardHeader>
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-4 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-32" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
              <Lightbulb className="w-5 h-5 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground" data-testid="heading-suggestions">
              Community Suggestions
            </h1>
          </div>
          <Button 
            onClick={() => setIsDialogOpen(true)}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
            data-testid="button-create-suggestion"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Suggestion
          </Button>
        </div>
        <p className="text-muted-foreground">
          Feature requests and improvement ideas from our community
        </p>
      </div>

      {/* Suggestions List */}
      {suggestions.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
              <Lightbulb className="w-8 h-8 text-muted-foreground" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2" data-testid="text-no-suggestions">
              No suggestions yet
            </h3>
            <p className="text-muted-foreground mb-4">
              Be the first to suggest a new feature or improvement!
            </p>
            <Button 
              onClick={() => setIsDialogOpen(true)}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
              data-testid="button-create-suggestion-empty"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Suggestion
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {suggestions.map((suggestion) => (
            <Card key={suggestion.id} data-testid={`card-suggestion-${suggestion.id}`}>
              <CardHeader className="pb-3">
                <div data-testid={`text-suggestion-content-${suggestion.id}`}>
                  <SuggestionContent 
                    content={suggestion.content}
                    screenshots={suggestion.screenshots}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span data-testid={`text-suggestion-author-${suggestion.id}`}>
                    {suggestion.displayName}
                  </span>
                  <span>•</span>
                  <span data-testid={`text-suggestion-date-${suggestion.id}`}>
                    {format(new Date(suggestion.createdAt), "MMM d, yyyy")}
                  </span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      
      <SubmitSuggestionDialog 
        open={isDialogOpen} 
        onOpenChange={setIsDialogOpen}
      />
    </div>
  );
}
